import { useCallback, useState } from 'react';
import { useCustom, useCustomMutation, useInvalidate, useList } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Phone, PhoneCall, Radio, UserMinus, UserPlus, Video } from 'lucide-react';
import { notify } from '@/lib/notify';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface ActiveCallParticipant {
  userId: number;
  role: 'HOST' | 'SPEAKER' | 'VIEWER';
  joinedAt: string;
  firstName: string | null;
  username: string | null;
  avatarUrl: string | null;
}

interface ActiveCallData {
  room: {
    id: string;
    type: 'CALL' | 'STAGE';
    hostUserId: number;
    livestreamId: number | null;
    startedAt: string | null;
  };
  participants: ActiveCallParticipant[];
}

interface ViewerInfo {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface LivestreamCallStageProps {
  livestreamId: number;
  isLive: boolean;
}

function displayName(user: { firstName: string | null; lastName?: string | null; username: string | null; userId: number }): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) { return fullName; }
  if (user.username) { return `@${user.username}`; }
  return `#${user.userId}`;
}

export function LivestreamCallStage({ livestreamId, isLive }: LivestreamCallStageProps) {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const [pendingCallUserId, setPendingCallUserId] = useState<number | null>(null);
  const [confirmKickUserId, setConfirmKickUserId] = useState<number | null>(null);

  const invalidateCall = useCallback(() => {
    void queryClient.invalidateQueries({ predicate: (q) => {
      const key = q.queryKey;
      return Array.isArray(key) && key.some((k) => typeof k === 'string' && k.includes('by-livestream'));
    }});
  }, [queryClient]);

  const { result: viewersResult, query: viewersQuery } = useList<ViewerInfo>({
    resource: `admin/livestreams/${livestreamId}/viewers`,
    pagination: { mode: 'off' },
    queryOptions: {
      enabled: isLive,
      // Реалтайм: livestream.viewers.changed инвалидирует /viewers.
      refetchInterval: 5 * 60_000,
    },
  });
  const viewers = viewersResult?.data ?? [];
  const viewersLoading = viewersQuery?.isLoading ?? false;

  const { result: callResult } = useCustom<ActiveCallData>({
    url: `/admin/calls/by-livestream/${livestreamId}`,
    method: 'get',
    queryOptions: {
      enabled: isLive,
      // Реалтайм: LiveKit webhook → livestream.call.changed → invalidate.
      refetchInterval: 5 * 60_000,
    },
  });
  const rawCallData = callResult?.data as ActiveCallData | null | undefined;
  const activeCall = rawCallData?.room ? rawCallData : null;

  const { mutate: initiateCall, mutation: initiateMutation } = useCustomMutation();
  const { mutate: kickParticipant, mutation: kickMutation } = useCustomMutation();
  const { mutate: createStage, mutation: createStageMutation } = useCustomMutation();

  const handleCreateAndOpen = useCallback(() => {
    const newWindow = window.open('about:blank', '_blank');
    createStage(
      {
        url: '/admin/calls/create-stage',
        method: 'post',
        values: { livestreamId },
      },
      {
        onSuccess: (resp) => {
          invalidateCall();
          const roomId = (resp as { data?: { roomId?: string } })?.data?.roomId;
          if (roomId && newWindow) {
            newWindow.location.href = `/livestreams/stage/${roomId}`;
          } else if (roomId) {
            window.open(`/livestreams/stage/${roomId}`, '_blank');
          }
        },
        onError: (err) => {
          newWindow?.close();
          notify.error(err.message || 'Не удалось создать сцену');
        },
      },
    );
  }, [createStage, invalidateCall, livestreamId]);

  const handleOpenStage = useCallback(() => {
    if (activeCall) {
      window.open(`/livestreams/stage/${activeCall.room.id}`, '_blank');
    }
  }, [activeCall]);

  const handleCall = useCallback(
    (userId: number) => {
      setPendingCallUserId(userId);
      initiateCall(
        {
          url: '/admin/calls/initiate',
          method: 'post',
          values: { inviteeUserId: userId, livestreamId },
        },
        {
          onSuccess: () => {
            notify.success('Звонок отправлен пользователю');
            invalidateCall();
            invalidate({ resource: `admin/livestreams/${livestreamId}/viewers`, invalidates: ['list'] });
            setPendingCallUserId(null);
          },
          onError: (err) => {
            notify.error(err.message || 'Не удалось инициировать звонок');
            setPendingCallUserId(null);
          },
        },
      );
    },
    [initiateCall, invalidate, invalidateCall, livestreamId],
  );

  const handleKick = useCallback(
    (userId: number) => {
      if (!activeCall) { return; }
      kickParticipant(
        {
          url: `/admin/calls/${activeCall.room.id}/participants/${userId}`,
          method: 'delete',
          values: {},
        },
        {
          onSuccess: () => {
            notify.success('Участник удалён из звонка');
            invalidateCall();
            setConfirmKickUserId(null);
          },
          onError: (err) => {
            notify.error(err.message || 'Не удалось удалить участника');
          },
        },
      );
    },
    [activeCall, invalidateCall, kickParticipant],
  );

  const callParticipants = activeCall?.participants ?? [];
  const callParticipantIds = new Set(callParticipants.map((p) => p.userId));
  const callableViewers = viewers.filter((v) => !callParticipantIds.has(v.userId));

  if (!isLive) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Сцена доступна только во время активной трансляции.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Radio className="h-4 w-4" />
            Статус сцены
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeCall ? (
            <div className="flex items-center gap-3">
              <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                Активна
              </Badge>
              <span className="text-sm text-muted-foreground">
                {callParticipants.length > 0
                  ? `${callParticipants.length} участник(ов)`
                  : 'Ожидание участников'}
              </span>
              <Button
                variant="default"
                size="sm"
                className="h-8 text-xs ml-auto"
                onClick={handleOpenStage}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Открыть сцену
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Badge variant="secondary">Не активна</Badge>
              <Button
                variant="default"
                size="sm"
                className="h-8 text-xs"
                disabled={createStageMutation.isPending}
                onClick={handleCreateAndOpen}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Активировать и открыть
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {activeCall && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-green-500" />
              Участники сцены ({callParticipants.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {callParticipants.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Никто ещё не подключился к сцене.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Участник</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>На сцене с</TableHead>
                    <TableHead className="w-32">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callParticipants.map((p) => (
                    <TableRow key={p.userId}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Video className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium text-sm">{displayName(p)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.role}</TableCell>
                      <TableCell className="text-sm">
                        {new Date(p.joinedAt).toLocaleTimeString('ru-RU')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          disabled={kickMutation.isPending}
                          onClick={() => setConfirmKickUserId(p.userId)}
                        >
                          <UserMinus className="h-3 w-3 mr-1" />
                          Убрать
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Пригласить зрителя на сцену
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {viewersLoading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Загрузка зрителей...
            </div>
          )}
          {!viewersLoading && callableViewers.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Нет зрителей, которых можно пригласить.
            </div>
          )}
          {!viewersLoading && callableViewers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Зритель</TableHead>
                  <TableHead className="w-32">Действие</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callableViewers.map((v) => (
                  <TableRow key={v.userId}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{displayName(v)}</span>
                        {v.username && (
                          <span className="text-xs text-muted-foreground">@{v.username}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={initiateMutation.isPending && pendingCallUserId === v.userId}
                        onClick={() => handleCall(v.userId)}
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        Позвонить
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmKickUserId !== null} onOpenChange={(open) => !open && setConfirmKickUserId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Убрать со сцены</DialogTitle>
            <DialogDescription>
              Участник будет отключён от звонка. Он сможет вернуться, только если его пригласят снова.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmKickUserId(null)} disabled={kickMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              disabled={kickMutation.isPending}
              onClick={() => confirmKickUserId !== null && handleKick(confirmKickUserId)}
            >
              <UserMinus className="h-4 w-4 mr-2" />
              Убрать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
